import { useEffect, useState } from 'react';
import { useCustomPreferenceOrDefault } from '../utils/api';
import { isServer } from '../utils/utils';
import type { CustomPreference } from '../utils/api/CustomPreference';

/**
 * A custom hook that syncs with global variables and listens for custom events.
 * @param variableName The name of the global variable to sync with
 * @param defaultValue The default value to use if the global variable is not set
 * @returns A state value and setter function
 */
export function useABTestVariable<T>(variableName: CustomPreference, defaultValue: T): T {
    const preferenceValue = useCustomPreferenceOrDefault<T>(variableName, defaultValue);
    const [state, setState] = useState<T | null>(
        isServer ? null : ((window[variableName] as T) ?? null),
    );

    useEffect(() => {
        if (isServer) return undefined;

        // Create event listener for custom events
        const eventName = `slt-abtest-variable-changed-${variableName}`;

        const handleStateChange = (event: CustomEvent<T>): void => {
            setState(event.detail);
        };

        // Add event listener
        window.addEventListener(eventName, handleStateChange as EventListener);

        // Cleanup
        return () => {
            window.removeEventListener(eventName, handleStateChange as EventListener);
        };
    }, [variableName, setState]);

    return state ?? preferenceValue;
}
